import {
  HHAlert,
  HHBox,
  HHCard,
  HHCardContent,
  HHCircularProgress,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import { Cancel, CheckCircle } from '@mui/icons-material';
import {
  ASSESSMENT_MATCHED_LABEL,
  ASSESSMENT_NOT_MATCHED_LABEL,
} from '../constants/partner-data-gateway.constant';
import { AssessmentMatch } from '../domain/types';
import { useIngestionAssessments } from '../hooks/use-ingestion-assessments';
import { printDateAndTime } from '../utils/print-date-and-time';
import { JsonViewer } from './json-viewer';

export const ASSESSMENTS_TAB_TEST_ID = 'ingestion-assessments-tab';
export const ASSESSMENTS_LOADING_TEST_ID = 'assessments-loading';
export const ASSESSMENTS_ERROR_TEST_ID = 'assessments-error-alert';
export const ASSESSMENTS_EMPTY_STATE_TEST_ID = 'assessments-empty-state';
export const ASSESSMENT_CARD_TEST_ID = 'assessment-verdict-card';

export const ASSESSMENTS_EMPTY_STATE_MESSAGE =
  'No configurations were assessed against this ingestion.';
export const ASSESSMENTS_ERROR_MESSAGE =
  'Could not load assessment verdicts. Please try again.';
export const ASSESSMENT_NO_REPORT_MESSAGE =
  'The Gatekeeper returned no report for this configuration.';

interface AssessmentVerdictCardProps {
  assessment: AssessmentMatch;
}

const AssessmentVerdictCard = ({
  assessment,
}: AssessmentVerdictCardProps): JSX.Element => {
  const { matched } = assessment;

  return (
    <HHCard
      variant="outlined"
      data-testid={ASSESSMENT_CARD_TEST_ID}
      // Stringified: React warns when a boolean is handed to a custom
      // attribute, and tests read the verdict back off the card.
      data-matched={String(matched)}
      // The accent colour is the at-a-glance signal when an operator is
      // scanning a stack of verdicts, so matched and unmatched cards must
      // not look alike.
      sx={{
        borderLeftWidth: 4,
        borderLeftStyle: 'solid',
        borderLeftColor: matched ? 'success.main' : 'error.main',
      }}
    >
      <HHCardContent>
        <HHStack direction="row" alignItems="center" gap={1}>
          {/* MUI icons are aria-hidden by default, so the verdict needs a
              text alternative — `titleAccess` renders an SVG <title>. */}
          {matched ? (
            <CheckCircle
              color="success"
              fontSize="small"
              titleAccess={ASSESSMENT_MATCHED_LABEL}
            />
          ) : (
            <Cancel
              color="error"
              fontSize="small"
              titleAccess={ASSESSMENT_NOT_MATCHED_LABEL}
            />
          )}
          <HHTypography hhVariant="h3">
            {`Configuration: ${assessment.configurationName}`}
          </HHTypography>
        </HHStack>

        <HHTypography hhVariant="body2" color="textSecondary">
          {`Status: ${assessment.configurationStatus} | Matched: ${
            matched ? 'Yes' : 'No'
          }`}
        </HHTypography>

        <HHBox marginY={1}>
          {assessment.report ? (
            <JsonViewer value={assessment.report} label="Assessment report" />
          ) : (
            <HHTypography hhVariant="body2" color="textSecondary">
              {ASSESSMENT_NO_REPORT_MESSAGE}
            </HHTypography>
          )}
        </HHBox>

        <HHTypography hhVariant="body2" color="textSecondary">
          {`Created: ${printDateAndTime(assessment.createdAt)}`}
        </HHTypography>
      </HHCardContent>
    </HHCard>
  );
};

export interface AssessmentsTabProps {
  eventId: string;
}

/**
 * The `?tab=assessments` panel: one card per Gatekeeper verdict.
 *
 * The overview tab shows the same verdicts in a summary card; this tab gives
 * each one enough room for its (opaque) assessment report, which is rendered
 * verbatim through `<JsonViewer />`.
 */
export const AssessmentsTab = ({
  eventId,
}: AssessmentsTabProps): JSX.Element => {
  const { assessments, isLoading, hasError } = useIngestionAssessments(eventId);

  return (
    <HHBox data-testid={ASSESSMENTS_TAB_TEST_ID}>
      {isLoading && (
        <HHBox
          data-testid={ASSESSMENTS_LOADING_TEST_ID}
          display="flex"
          justifyContent="center"
          paddingY={4}
        >
          <HHCircularProgress />
        </HHBox>
      )}

      {!isLoading && hasError && (
        <HHBox data-testid={ASSESSMENTS_ERROR_TEST_ID} paddingY={2}>
          <HHAlert hhVariant="outlined" severity="error">
            {ASSESSMENTS_ERROR_MESSAGE}
          </HHAlert>
        </HHBox>
      )}

      {/* Only claim "none" once the fetch has actually succeeded — saying so
          after a failure would be actively misleading. */}
      {!isLoading && !hasError && assessments.length === 0 && (
        <HHBox data-testid={ASSESSMENTS_EMPTY_STATE_TEST_ID} paddingY={4}>
          <HHTypography hhVariant="body2" color="textSecondary">
            {ASSESSMENTS_EMPTY_STATE_MESSAGE}
          </HHTypography>
        </HHBox>
      )}

      {!isLoading && !hasError && (
        <HHStack gap={2}>
          {assessments.map(assessment => (
            <AssessmentVerdictCard
              key={assessment.id}
              assessment={assessment}
            />
          ))}
        </HHStack>
      )}
    </HHBox>
  );
};
